import { supabase } from '../lib/supabase';

class ActivityLogService {
  constructor() {
    this.sessionId = this.generateSessionId();
    this.startTime = Date.now();
    this.lastActivity = Date.now();
    this.pendingLogs = [];
    this.batchSize = 10;
    this.flushInterval = 5000; // 5 segundos
    
    // Iniciar flush automático
    this.startAutoFlush();
    
    // Capturar dados do navegador
    this.browserInfo = this.getBrowserInfo();
  }

  generateSessionId() {
    // Gerar UUID v4 válido
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  getBrowserInfo() {
    return {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      cookieEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
  }

  async logEvent(alunoId, tipoEvento, categoria, detalhes = {}, duracaoMs = null) {
    try {
      console.log('🔍 ActivityLogService.logEvent chamado:', {
        alunoId,
        tipoEvento,
        categoria,
        detalhes,
        duracaoMs
      });

      if (!alunoId) {
        console.warn('⚠️ Log ignorado: alunoId não fornecido');
        return false;
      }

      const logEntry = {
        aluno_id: alunoId,
        tipo_evento: tipoEvento,
        categoria: categoria,
        detalhes: {
          ...detalhes,
          url: window.location.href,
          referrer: document.referrer,
          timestamp_client: new Date().toISOString(),
          browser_info: this.browserInfo
        },
        sessao_id: this.sessionId,
        duracao_ms: duracaoMs
      };

      console.log('📝 Log entry criado:', logEntry);

      // Adicionar à fila de logs pendentes
      this.pendingLogs.push(logEntry);
      this.lastActivity = Date.now();

      // Flush imediato para eventos críticos
      const criticalEvents = ['inicio_teste', 'finalizacao_teste', 'erro_carregamento', 'logout_aluno'];
      if (criticalEvents.includes(tipoEvento)) {
        await this.flushLogs();
      }

      // Flush automático quando atingir o tamanho do batch
      if (this.pendingLogs.length >= this.batchSize) {
        await this.flushLogs();
      }

      return true;
    } catch (error) {
      console.error('Erro ao criar log de atividade:', error);
      return false;
    }
  }

  async flushLogs() {
    if (this.pendingLogs.length === 0) {
      console.log('🔍 FlushLogs: Nenhum log pendente');
      return;
    }

    try {
      const logsToSend = [...this.pendingLogs];
      this.pendingLogs = [];

      console.log(`🚀 Enviando ${logsToSend.length} logs para o banco:`, logsToSend);

      const { data, error } = await supabase
        .from('logs_atividade_aluno')
        .insert(logsToSend)
        .select();

      if (error) {
        console.error('❌ Erro ao salvar logs:', error);
        console.error('❌ Detalhes do erro:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        // Recolocar logs na fila em caso de erro
        this.pendingLogs.unshift(...logsToSend);
        return false;
      }

      console.log(`✅ ${logsToSend.length} logs salvos com sucesso:`, data);
      return true;
    } catch (error) {
      console.error('❌ Erro no flush de logs:', error);
      return false;
    }
  }

  startAutoFlush() {
    setInterval(() => {
      if (this.pendingLogs.length > 0) {
        this.flushLogs();
      }
    }, this.flushInterval);

    // Flush ao sair da página
    window.addEventListener('beforeunload', () => {
      if (this.pendingLogs.length > 0) {
        // Usar sendBeacon para envio assíncrono
        navigator.sendBeacon('/api/logs', JSON.stringify(this.pendingLogs));
      }
    });

    // Flush quando a página perde o foco
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden' && this.pendingLogs.length > 0) {
        this.flushLogs();
      }
    });
  }

  // Métodos específicos para diferentes tipos de eventos

  async logSessao(alunoId, tipo, detalhes = {}) {
    return this.logEvent(alunoId, tipo, 'sessao', detalhes);
  }

  async logNavegacao(alunoId, tipo, detalhes = {}) {
    return this.logEvent(alunoId, tipo, 'navegacao', detalhes);
  }

  async logTeste(alunoId, tipo, detalhes = {}, duracao = null) {
    return this.logEvent(alunoId, tipo, 'teste', detalhes, duracao);
  }

  async logChat(alunoId, tipo, detalhes = {}, duracao = null) {
    return this.logEvent(alunoId, tipo, 'chat', detalhes, duracao);
  }

  async logPerfil(alunoId, tipo, detalhes = {}) {
    return this.logEvent(alunoId, tipo, 'perfil', detalhes);
  }

  async logPerformance(alunoId, tipo, detalhes = {}, duracao = null) {
    return this.logEvent(alunoId, tipo, 'performance', detalhes, duracao);
  }

  async logInteracao(alunoId, tipo, detalhes = {}) {
    return this.logEvent(alunoId, tipo, 'interacao', detalhes);
  }

  // Métodos utilitários para medir tempo

  startTimer(name) {
    this.timers = this.timers || {};
    this.timers[name] = Date.now();
  }

  endTimer(name) {
    if (!this.timers || !this.timers[name]) return 0;
    const duration = Date.now() - this.timers[name];
    delete this.timers[name];
    return duration;
  }

  // Método para capturar erros automaticamente
  async logError(alunoId, error, contexto = {}) {
    return this.logEvent(alunoId, 'erro_aplicacao', 'performance', {
      error_message: error.message,
      error_stack: error.stack,
      contexto: contexto
    });
  }

  // Método para logs de debug (apenas em desenvolvimento)
  async logDebug(alunoId, tipo, detalhes = {}) {
    if (process.env.NODE_ENV === 'development') {
      console.log(`🔍 DEBUG LOG: ${tipo}`, detalhes);
      return this.logEvent(alunoId, tipo, 'debug', detalhes);
    }
  }

  // Estatísticas da sessão atual
  getSessionStats() {
    return {
      sessionId: this.sessionId,
      duration: Date.now() - this.startTime,
      pendingLogs: this.pendingLogs.length,
      lastActivity: this.lastActivity,
      timeSinceLastActivity: Date.now() - this.lastActivity
    };
  }

  // Limpar dados da sessão
  clearSession() {
    this.flushLogs();
    this.sessionId = this.generateSessionId();
    this.startTime = Date.now();
    this.lastActivity = Date.now();
    this.timers = {};
  }
}

// Instância singleton
const activityLogService = new ActivityLogService();

export default activityLogService;
